import React from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { Loader2 } from 'lucide-react';
interface AnalyticsChartProps {
  title: string;
  type: 'line' | 'area' | 'bar' | 'pie';
  data: any[];
  dataKey: string;
  xAxisKey?: string;
  color?: string;
  height?: number;
  isLoading?: boolean;
}
const COLORS = ['#10B981', '#F59E0B', '#EF4444', '#3B82F6'];
export function AnalyticsChart({
  title,
  type,
  data,
  dataKey,
  xAxisKey = 'name',
  color = '#1D4ED8',
  height = 300,
  isLoading = false
}: AnalyticsChartProps) {
  if (isLoading) {
    return <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col items-center justify-center transition-colors" style={{
      height: height + 80
    }} data-id="element-193">
        <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin mb-2" data-id="element-194" />
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-195">
          Loading data...
        </p>
      </div>;
  }
  const renderChart = () => {
    switch (type) {
      case 'line':
        return <LineChart data={data} margin={{
          top: 5,
          right: 10,
          bottom: 5,
          left: -10
        }} data-id="element-196">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" className="dark:stroke-gray-700" data-id="element-197" />
            <XAxis dataKey={xAxisKey} axisLine={false} tickLine={false} tick={{
            fill: '#6B7280',
            fontSize: 11
          }} dy={8} data-id="element-198" />
            <YAxis axisLine={false} tickLine={false} tick={{
            fill: '#6B7280',
            fontSize: 11
          }} width={45} data-id="element-199" />
            <Tooltip contentStyle={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            fontSize: '12px'
          }} itemStyle={{
            color: '#111827',
            fontWeight: 500
          }} data-id="element-200" />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={{
            r: 3,
            fill: color,
            strokeWidth: 2,
            stroke: '#fff'
          }} activeDot={{
            r: 5
          }} data-id="element-201" />
          </LineChart>;
      case 'area':
        return <AreaChart data={data} margin={{
          top: 5,
          right: 10,
          bottom: 5,
          left: -10
        }} data-id="element-202">
            <defs data-id="element-203">
              <linearGradient id={`color${dataKey}`} x1="0" y1="0" x2="0" y2="1" data-id="element-204">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} data-id="element-205" />
                <stop offset="95%" stopColor={color} stopOpacity={0} data-id="element-206" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" className="dark:stroke-gray-700" data-id="element-207" />
            <XAxis dataKey={xAxisKey} axisLine={false} tickLine={false} tick={{
            fill: '#6B7280',
            fontSize: 11
          }} dy={8} data-id="element-208" />
            <YAxis axisLine={false} tickLine={false} tick={{
            fill: '#6B7280',
            fontSize: 11
          }} width={45} data-id="element-209" />
            <Tooltip contentStyle={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            fontSize: '12px'
          }} data-id="element-210" />
            <Area type="monotone" dataKey={dataKey} stroke={color} fillOpacity={1} fill={`url(#color${dataKey})`} strokeWidth={2} data-id="element-211" />
          </AreaChart>;
      case 'bar':
        return <BarChart data={data} margin={{
          top: 5,
          right: 10,
          bottom: 5,
          left: -10
        }} data-id="element-212">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" className="dark:stroke-gray-700" data-id="element-213" />
            <XAxis dataKey={xAxisKey} axisLine={false} tickLine={false} tick={{
            fill: '#6B7280',
            fontSize: 11
          }} dy={8} data-id="element-214" />
            <YAxis axisLine={false} tickLine={false} tick={{
            fill: '#6B7280',
            fontSize: 11
          }} width={45} data-id="element-215" />
            <Tooltip cursor={{
            fill: '#F3F4F6'
          }} contentStyle={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            fontSize: '12px'
          }} data-id="element-216" />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={40} data-id="element-217" />
          </BarChart>;
      case 'pie':
        return <PieChart data-id="element-218">
            <Pie data={data} cx="50%" cy="45%" innerRadius={50} outerRadius={75} paddingAngle={4} dataKey={dataKey} data-id="element-219">
              {data.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} data-id="element-220" />)}
            </Pie>
            <Tooltip contentStyle={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            fontSize: '12px'
          }} data-id="element-221" />
            <Legend verticalAlign="bottom" height={40} iconType="circle" wrapperStyle={{
            fontSize: '11px'
          }} data-id="element-222" />
          </PieChart>;
    }
  };
  return <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 h-full transition-colors" data-id="element-223">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 truncate" data-id="element-224">
        {title}
      </h3>
      <div style={{
      width: '100%',
      height: height
    }} data-id="element-225">
        <ResponsiveContainer data-id="element-226">{renderChart()}</ResponsiveContainer>
      </div>
    </div>;
}